import { useState, useEffect } from 'react';
import WholesaleLayout, { useWholesaleAuth } from './WholesaleLayout';
import { getWholesaleConfig, saveWholesaleConfig, uploadWholesaleLogo } from '../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WholesaleAdminSettings() {
  const user = useWholesaleAuth('admin');
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (user) getWholesaleConfig().then(r => setCfg(r.data)).catch(() => {}); }, [user]);

  const set = (key, val) => setCfg(c => ({ ...c, [key]: val }));
  const setNum = (key, val) => setCfg(c => ({ ...c, [key]: parseInt(val) || 0 }));

  const handleSave = async () => {
    setSaving(true);
    try { await saveWholesaleConfig(cfg); alert('Settings saved'); } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await uploadWholesaleLogo(fd);
      setCfg(c => ({ ...c, logo_path: res.data.logo_path }));
    } catch { alert('Logo upload failed'); }
  };

  if (!cfg) return <WholesaleLayout user={user} title="Settings"><div style={{ color:'#888' }}>Loading...</div></WholesaleLayout>;

  const numPreview = (prefix, next, suffix, pad) => {
    const n = String(next || 0);
    const padded = pad ? n.padStart(pad, '0') : n;
    return `${prefix || ''}${padded}${suffix || ''}`;
  };

  return (
    <WholesaleLayout user={user} title="Settings">
      {/* Card 1: Company Details */}
      <div style={card}>
        <div style={cardTitle}>Vardhman Marketing Company Details</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          {[['vm_name','Company Name'],['vm_email','Email'],['vm_phone','Phone'],['vm_website','Website'],['vm_iec','IEC'],['vm_ad_code','AD Code'],['vm_gst','GST']].map(([k,l]) => (
            <div key={k}><label style={labelStyle}>{l}</label><input value={cfg[k]||''} onChange={e => set(k, e.target.value)} style={inputStyle} data-testid={`cfg-${k}`} /></div>
          ))}
          <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Address</label><input value={cfg.vm_address||''} onChange={e => set('vm_address', e.target.value)} style={inputStyle} /></div>
        </div>
        <div style={{ marginTop:16, display:'flex', alignItems:'center', gap:16 }}>
          <label style={labelStyle}>Logo</label>
          {cfg.logo_path && <img src={`${API}/api/static/${cfg.logo_path}`} alt="logo" style={{ height:48, objectFit:'contain' }} />}
          <input type="file" accept="image/*" onChange={handleLogo} data-testid="logo-upload" />
        </div>
      </div>

      {/* Card 2: Bank Details */}
      <div style={{ ...card, marginTop:24 }}>
        <div style={cardTitle}>Bank Details</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          {[['bank_beneficiary','Beneficiary Name'],['bank_name','Bank Name'],['bank_account','Account Number'],['bank_ifsc','IFSC Code'],['bank_swift','SWIFT Code'],['bank_routing','Routing Number']].map(([k,l]) => (
            <div key={k}><label style={labelStyle}>{l}</label><input value={cfg[k]||''} onChange={e => set(k, e.target.value)} style={inputStyle} /></div>
          ))}
          <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Bank Address</label><input value={cfg.bank_address||''} onChange={e => set('bank_address', e.target.value)} style={inputStyle} /></div>
        </div>
        <div style={{ marginTop:16, background:'#F7F6F2', padding:16, borderRadius:2, fontSize:12, lineHeight:2 }}>
          <strong>Preview:</strong><br/>
          Beneficiary: {cfg.bank_beneficiary}<br/>
          Bank: {cfg.bank_name}<br/>
          Account: {cfg.bank_account}<br/>
          IFSC: {cfg.bank_ifsc} | SWIFT: {cfg.bank_swift}<br/>
          Address: {cfg.bank_address}
        </div>
      </div>

      {/* Card 3: Numbering */}
      <div style={{ ...card, marginTop:24 }}>
        <div style={cardTitle}>Invoice & Order Numbering</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:24 }}>
          {[
            { title:'Orders', color:'#111', prefix:'ord_prefix', suffix:'ord_suffix', next:'ord_next', pad:'ord_pad' },
            { title:'Proforma Invoice', color:'#C41E2A', prefix:'pi_prefix', suffix:'pi_suffix', next:'pi_next', pad:'pi_pad' },
            { title:'Commercial Invoice', color:'#003366', prefix:'ci_prefix', suffix:'ci_suffix', next:'ci_next', pad:'ci_pad' },
          ].map(col => (
            <div key={col.title}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:14, fontWeight:700, letterSpacing:1, marginBottom:12, paddingBottom:8, borderBottom:`3px solid ${col.color}`, color:col.color }}>{col.title}</div>
              <div style={{ marginBottom:10 }}><label style={labelStyle}>Prefix</label><input value={cfg[col.prefix]||''} onChange={e => set(col.prefix, e.target.value)} style={inputStyle} /></div>
              <div style={{ marginBottom:10 }}><label style={labelStyle}>Suffix</label><input value={cfg[col.suffix]||''} onChange={e => set(col.suffix, e.target.value)} style={inputStyle} /></div>
              <div style={{ marginBottom:10 }}><label style={labelStyle}>Next Number</label><input type="number" min="1" value={cfg[col.next]||''} onChange={e => setNum(col.next, e.target.value)} style={inputStyle} /></div>
              <div style={{ marginBottom:10 }}><label style={labelStyle}>Zero Padding</label><input type="number" min="0" max="8" value={cfg[col.pad]||0} onChange={e => setNum(col.pad, e.target.value)} style={inputStyle} /></div>
              <div style={{ fontSize:12, color:'#888', padding:'8px 12px', background:'#F7F6F2', borderRadius:2 }}>Preview: <strong>{numPreview(cfg[col.prefix], cfg[col.next], cfg[col.suffix], cfg[col.pad])}</strong></div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop:24 }}>
        <button onClick={handleSave} disabled={saving} data-testid="save-settings-btn" style={btnPrimary}>{saving ? 'Saving...' : 'Save All Settings'}</button>
      </div>
    </WholesaleLayout>
  );
}

const card = { background:'#fff', border:'1px solid #E0DED8', borderRadius:4, padding:24 };
const cardTitle = { fontFamily:"'Barlow Condensed',sans-serif", fontSize:16, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', marginBottom:16 };
const labelStyle = { display:'block', fontSize:11, letterSpacing:1.5, textTransform:'uppercase', fontWeight:600, color:'#888', marginBottom:6 };
const inputStyle = { width:'100%', padding:'10px 12px', border:'1px solid #E0DED8', borderRadius:2, fontFamily:'Barlow', fontSize:13, outline:'none' };
const btnPrimary = { display:'inline-flex', alignItems:'center', gap:6, padding:'10px 24px', fontFamily:'Barlow', fontSize:12, fontWeight:600, letterSpacing:1, textTransform:'uppercase', borderRadius:2, cursor:'pointer', background:'#C41E2A', color:'#fff', border:'none' };
